import { Injectable } from '@angular/core';
import { InMemoryCache } from '@apollo/client/core';
import { Authenticated } from '@app/auth/actions/auth';
import * as fromAuth from '@app/auth/reducers';
import { FetchAppConfig, FetchAppInfo } from '@app/core/actions/config';
import { FeatureGuide } from '@app/core/config/feature-guide';
import * as fromRoot from '@app/reducer';
import { select, Store } from '@ngrx/store';
import { constructRestUrl } from '@util';
import { Apollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { FeatureGuideService } from 'pcs-component-library';
import * as R from 'ramda';
import { combineLatest, merge } from 'rxjs';
import { filter, take } from 'rxjs/operators';

const linkOptions = (prefix: string) => ({uri: constructRestUrl('graphql', prefix)});

@Injectable()
export class BootstrapService {

  private createApollo;

  constructor(
    private apollo: Apollo, private httpLink: HttpLink,
    private store: Store<fromAuth.State>,
    private featureGuideService: FeatureGuideService) {
    const apolloClient = this.apollo.create.bind(this.apollo);
    const apolloLink = this.httpLink.create.bind(this.httpLink);
    const apolloOptions = (link => ({cache: new InMemoryCache({addTypename: false}), link}));

    this.createApollo = R.compose(apolloClient, apolloOptions, apolloLink, linkOptions);
  }

  /**
   * Bootstraps the application with needed server data.
   *
   * @return {Promise<any>}
   */
  async bootstrap(): Promise<any> {
    this.store.dispatch(new FetchAppConfig());
    this.store.dispatch(new FetchAppInfo());
    this.featureGuideService.initialize([
      FeatureGuide.PreviousNextNavigation,
      FeatureGuide.CalendarSelectionHint,
      FeatureGuide.CalendarDeselectionHint,
      FeatureGuide.CalendarSelectionExplanation
    ], []);

    const notNil = R.compose(R.not, R.isNil);


    return new Promise((resolve) => {
      const appConfig$ = this.store.pipe(select(fromRoot.getAppConfig)).pipe(
        filter(notNil),
        take(1)
      );
      const appInfo$ = this.store.pipe(select(fromRoot.getAppInfo)).pipe(
        filter(notNil),
        take(1)
      );
      const configLoaded$ = combineLatest([appConfig$, appInfo$]);
      const loggedIn$ = this.store.pipe(select(fromAuth.getLoggedIn));
      const authError$ = this.store.pipe(select(fromAuth.getAuthError));
      const loggedInOrAuthError$ = merge(loggedIn$, authError$).pipe(
        filter(loggedInOrAuthError => loggedInOrAuthError),
        take(1)
      );

      configLoaded$.subscribe(() => this.store.dispatch(new Authenticated()));
      appConfig$.subscribe(() => this.createApollo());

      loggedInOrAuthError$.subscribe(isLoggedIn =>
        resolve(isLoggedIn)
      );
    });
  }
}
